import React, { useState } from 'react';
import { 
  Box, 
  CssBaseline, 
  Drawer, 
  List, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Divider,
  Tooltip,
  Stack,
  styled,
  alpha,
  useTheme,
  AppBarProps,
  ToolbarProps
} from '@mui/material';
import { 
  Dashboard, 
  People, 
  LocalShipping, 
  Map, 
  Assessment, 
  AccountCircle,
  Settings,
  Logout,
  Menu as MenuIcon,
  Business,
  Group,
  RequestQuote
} from '@mui/icons-material';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import Logo from '../components/common/Logo';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const drawerWidth = 280;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.customShadows.z1,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledToolbar = styled(Toolbar)<ToolbarProps>(({ theme }) => ({
  padding: `${theme.spacing(0, 3)} !important`,
  minHeight: '72px !important',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(0, 2)} !important`,
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '48px', // Account for mobile menu button
  },
}));

const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginTop: '72px',
  minHeight: 'calc(100vh - 72px)',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StyledDrawer = React.forwardRef<HTMLDivElement>((props, ref) => (
  <Drawer ref={ref} {...props} />
));

StyledDrawer.displayName = 'StyledDrawer';

const StyledDrawerRoot = styled(Drawer)(({ theme }) => ({ 
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    marginTop: '72px',
    borderRight: 'none',
    background: theme.palette.background.paper,
    boxShadow: theme.customShadows.z1,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.primary.main, 0.2),
      borderRadius: '3px',
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.3),
      },
    },
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'transform 0.2s ease-in-out',
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 220,
    borderRadius: 12,
    marginTop: theme.spacing(1.5),
    boxShadow: theme.customShadows.z12,
    '& .MuiMenu-list': {
      padding: theme.spacing(1, 0),
    },
    '& .MuiMenuItem-root': {
      padding: theme.spacing(1, 2),
      '& .MuiSvgIcon-root': {
        marginRight: theme.spacing(1.5),
        fontSize: '1.25rem',
        color: theme.palette.text.secondary,
      },
    },
  },
}));

export default function DashboardLayout() {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const open = Boolean(anchorEl);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
    setMobileOpen(false);
  };

  // Define all menu items with role restrictions
  const allMenuItems = [
    { key: 'dashboard', icon: <Dashboard />, path: '/dashboard', roles: ['admin', 'driver'] },
    { key: 'users', icon: <People />, path: '/users', roles: ['admin'] },
    { key: 'customers', icon: <Business />, path: '/customers', roles: ['admin'] },
    { key: 'staff', icon: <Group />, path: '/staff', roles: ['admin'] },
    { 
      key: 'drivers', 
      icon: <LocalShipping />, 
      path: user?.role === 'driver' ? '/drivers/status' : '/drivers', 
      roles: ['admin', 'driver'] 
    },
    { key: 'shipments', icon: <LocalShipping />, path: '/shipments', roles: ['admin'] },
    { key: 'quotes', icon: <RequestQuote />, path: '/quotes', roles: ['admin'] },
    { key: 'zones', icon: <Map />, path: '/zones', roles: ['admin'] },
    { key: 'reports', icon: <Assessment />, path: '/reports', roles: ['admin'] },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    user?.role && item.roles.includes(user.role)
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <StyledAppBar position="fixed">
        <StyledToolbar>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            position: 'relative',
            height: '100%',
          }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { sm: 'none' },
                position: 'relative',
                zIndex: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
            <LogoContainer>
              <Logo variant="default" />
            </LogoContainer>
          </Box>
          
          <Stack direction="row" spacing={2} alignItems="center">
            <LanguageSwitcher />
            <Tooltip title={user?.name || 'User'}>
              <IconButton
                onClick={handleMenu}
                size="small"
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{ ml: 1 }}
              >
                <StyledAvatar>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </StyledAvatar>
              </IconButton>
            </Tooltip>
          </Stack>
        </StyledToolbar>
      </StyledAppBar>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name || t('common.user')}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primary={t('dashboard.profile')} />
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary={t('dashboard.logout')} />
        </MenuItem>
      </StyledMenu>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <StyledDrawerRoot
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: theme.palette.background.paper,
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Logo variant="sidebar" />
          </Box>
          <Divider />
          <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 180px)' }}>
            <List>
              {menuItems.map((item) => {
                const isSelected = location.pathname.startsWith(item.path);
                return (
                  <ListItemButton
                    key={item.key}
                    component={Link}
                    to={item.path}
                    selected={isSelected}
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      borderRadius: 2,
                      mx: 1.5,
                      my: 0.5,
                      '&.Mui-selected': {
                        color: 'primary.main',
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main',
                        },
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        },
                      },
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                      {React.cloneElement(item.icon, {
                        color: isSelected ? 'primary' : 'inherit',
                      })}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`menu.${item.key}`)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </StyledDrawerRoot>
        
        <StyledDrawerRoot
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: theme.palette.background.paper,
            },
          }}
          open
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Logo variant="sidebar" />
          </Box>
          <Divider />
          <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 180px)' }}>
            <List>
              {menuItems.map((item) => {
                const isSelected = location.pathname.startsWith(item.path);
                return (
                  <ListItemButton
                    key={item.key}
                    component={Link}
                    to={item.path}
                    selected={isSelected}
                    sx={{
                      borderRadius: 2,
                      mx: 1.5,
                      my: 0.5,
                      '&.Mui-selected': {
                        color: 'primary.main',
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main',
                        },
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        },
                      },
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                      {React.cloneElement(item.icon, {
                        color: isSelected ? 'primary' : 'inherit',
                      })}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`menu.${item.key}`)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </StyledDrawerRoot>
      </Box>
      <MainContent>
        <Outlet />
      </MainContent>
    </Box>
  );
}


